import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import {
  SwimmingBooking,
  SwimmingBookingPayload,
  SwimmingChangeMemberPayload,
  SwimmingSchedule,
} from '@/types/swimming'

export const SwimmingBookingService = (client: AxiosInstance) => {
  return {
    async createBooking(data: SwimmingBookingPayload): Promise<SwimmingBooking> {
      const { data: responseData } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/bookings/swimming', data)
      )
      return deserialize(responseData, SwimmingBooking)
    },

    async cancelBooking(
      id: string | number,
      opts?: { branchIdOverride?: number }
    ): Promise<boolean> {
      // Branch-scoped on the BE; cross-branch lists (member swimming history)
      // pass the booking's own branch so the row doesn't 404.
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/bookings/swimming/${id}/cancel`, undefined, config)
      )
      return !!data?.success
    },

    async rescheduleBooking(id: string | number, scheduleId: string | number): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/bookings/swimming/${id}/reschedule`, { scheduleId })
      )
      return !!data?.success
    },

    async rescheduleToTime(
      id: string | number,
      payload: {
        startTime: string
        endTime: string
        keepSameCoach?: boolean
        trainerId?: number | null
      }
    ): Promise<SwimmingBooking> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/bookings/swimming/${id}/reschedule-to-time`, payload)
      )
      return deserialize(data, SwimmingBooking)
    },

    async changeBookingMember(
      id: string | number,
      payload: SwimmingChangeMemberPayload
    ): Promise<SwimmingBooking> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/bookings/swimming/${id}/change-member`, payload)
      )
      return deserialize(data, SwimmingBooking)
    },

    async getBooking(id: string | number): Promise<SwimmingBooking> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/bookings/swimming/${id}`)
      )
      return deserialize(data, SwimmingBooking)
    },

    async checkIn(id: string | number, opts?: { branchIdOverride?: number }): Promise<boolean> {
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/bookings/swimming/${id}/check-in`, undefined, config)
      )
      return !!data?.success
    },

    async removeCheckIn(
      id: string | number,
      opts?: { branchIdOverride?: number }
    ): Promise<boolean> {
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/bookings/swimming/${id}/check-in`, config)
      )
      return !!data?.success
    },

    async getAvailableRescheduleLists(
      bookingId: string | number,
      targetDate: string,
      isSameTrainer?: boolean
    ): Promise<SwimmingSchedule[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(
          `/bookings/swimming/${bookingId}/available-reschedule-list`,
          {
            params: { targetDate, isSameTrainer },
          }
        )
      )
      return data?.map((item: Record<string, unknown>) => deserialize(item, SwimmingSchedule))
    },
  }
}
